import { createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "src/services/api-client";
import { NotificationService } from "src/helpers/notifications";
import {
  CloneFields,
  ICloneSettingsFormValues,
} from "src/page-components/cabinet/my-clones/CloneSettings/types";
import {
  successfulResponsesTrns,
  pendingResponsesTrns,
  errorResponsesTrns,
} from "src/translate/responses";
import { CloneStatus, IClone } from "src/@types/clones";
import { IResponseWithPagination } from "src/@types/api";

export const ITEMS_PER_PAGE = 10;
export const MY_CLONES_SLICE_NAME = "myClones";

export const getMyClonesAsync = createAsyncThunk(
  `${MY_CLONES_SLICE_NAME}/fetchMyClones`,
  async (params: string = "", { rejectWithValue }) => {
    try {
      const { data } = await instance.get<IResponseWithPagination<IClone>>(
        `/clones/my-clones/?${params}&page_size=${ITEMS_PER_PAGE}`
      );

      return data;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.error(errorText || errorResponsesTrns.myClones);

      return rejectWithValue(errorText);
    }
  }
);

interface CreateCloneParams {
  values: ICloneSettingsFormValues;
  onSuccess?: (clone: IClone) => void;
}
export const createCloneAsync = createAsyncThunk(
  `${MY_CLONES_SLICE_NAME}/createClone`,
  async ({ values, onSuccess }: CreateCloneParams, { rejectWithValue }) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.createClone
    );

    try {
      const { company_presentation, logo, main_photo, ...cloneData } = values;

      const { data } = await instance.post<IClone>("/clones/", cloneData);

      const formData = new FormData();

      const cloneMedia = { logo, main_photo };

      Object.entries(cloneMedia).forEach(([field, file]) =>
        formData.append(field, file)
      );

      company_presentation.forEach((file) =>
        formData.append(CloneFields.companyPresentation, file)
      );

      await instance.post(`/clones/${data.id}/create-files/`, formData);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.createClone
      );

      onSuccess?.(data);
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.createClone
      );

      return rejectWithValue(errorText);
    }
  }
);

interface EditCloneParams {
  values: ICloneSettingsFormValues;
  id: string;
  onSuccess?: () => void;
}
export const editCloneAsync = createAsyncThunk(
  `${MY_CLONES_SLICE_NAME}/editClone`,
  async ({ values, id, onSuccess }: EditCloneParams, { rejectWithValue }) => {
    const toastId = NotificationService.loading(pendingResponsesTrns.editClone);

    try {
      const { company_presentation, logo, main_photo, ...cloneData } = values;

      const { data } = await instance.patch<{ id: number }>(
        `/clones/${id}/`,
        cloneData
      );

      const formData = new FormData();

      const cloneMedia = { logo, main_photo };

      Object.entries(cloneMedia).forEach(([field, file]) => {
        if (file) formData.append(field, file);
      });

      company_presentation.forEach((file) => {
        if (file) formData.append(CloneFields.companyPresentation, file);
      });

      await instance.patch(`/clones/${data.id}/update-files/`, formData);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.editClone
      );

      onSuccess?.();
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.editClone
      );

      return rejectWithValue(errorText);
    }
  }
);

export const deleteCloneAsync = createAsyncThunk(
  `${MY_CLONES_SLICE_NAME}/deleteClone`,
  async (id: number, { rejectWithValue }) => {
    const toastId = NotificationService.loading(
      pendingResponsesTrns.deleteClone
    );

    try {
      await instance.delete(`/clones/${id}/`);

      NotificationService.updateToSuccess(
        toastId,
        successfulResponsesTrns.deleteClone
      );
      return id;
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        toastId,
        errorText || errorResponsesTrns.deleteClone
      );

      return rejectWithValue(errorText);
    }
  }
);
interface ChangeCloneStatusParams {
  id: number;
  status: CloneStatus;
  onFinally?: VoidFunction;
}
export const changeCloneStatusAsync = createAsyncThunk(
  `${MY_CLONES_SLICE_NAME}/changeCloneStatus`,
  async (
    { id, status, onFinally }: ChangeCloneStatusParams,
    { rejectWithValue }
  ) => {
    try {
      await instance.patch(`/clones/${id}/`, { status });

      return { id, status };
    } catch ({ response }) {
      const errorText = response?.data?.error;

      NotificationService.updateToError(
        errorText || errorResponsesTrns.default
      );

      return rejectWithValue(errorText);
    } finally {
      onFinally?.();
    }
  }
);
